import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { useSearchParams } from "react-router-dom";

import classes from "./mastersFilterPage.module.css";

import {
  selectMastersCategoryFilterState,
  setFilters,
} from "../../../store/mastersCategoryFilterSlice";

import { setVariantFilters } from "../../../store/mastersVariantFilterSlice";
import { onOpen } from "../../../store/variantModalSlice";

import mobile_filter from "../../../assets/mobile_filter.svg";

import {
  selectAllMasterFilters,
  useGetMasterFiltersQuery,
} from "../../../services/mastersFilterApiSlice";

import { SpareBrandModelFilterOptions } from "../../../component/mastersFilters/mastersSparesFilters/SpareBrandModelOptions";
import { SparePartOptions } from "../../../component/mastersFilters/mastersSparesFilters/SparePartOptions";

// -------------------- ANIMATION CONFIG --------------------
const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const modalVariants = {
  hidden: { x: "-100%" },
  visible: {
    x: 0,
    transition: { type: "tween", duration: 0.35 },
  },
  exit: { x: "-100%", transition: { duration: 0.25 } },
};

// ==========================================================
//                    MAIN COMPONENT
// ==========================================================
export const MastersFiltersPage = () => {

  
  const dispatch = useDispatch();
  const filterRefs = useRef({});
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedFilterKey, setSelectedFilterKey] = useState(null);
  const [modalFilters, setModalFilters] = useState({});
  const [isOpen, setIsOpen] = useState(false);

  const masterCategoryState = useSelector(selectMastersCategoryFilterState);
  const { category, brand, model, part_name } = masterCategoryState;
  const isSpares = category === "spares";

  // -------------- Fetch Filters Using RTK Query --------------
  const { data, isSuccess } = useGetMasterFiltersQuery({ category });
  const filters = isSuccess ? selectAllMasterFilters(data) : [];

  // Quick access filters
  const quickTrayFilters = useMemo(
    () => filters.filter((f) => f.is_quick_access),
    [filters]
  );

  // --------------------- UTILS ---------------------
  const isFilterActive = (filter) => {
    const f = masterCategoryState;

    switch (filter.filter) {
      case "brand":
        return Boolean(f.brand?.length) || Boolean(f.model?.length);
      case "spare":
        return Boolean(f.spare?.length);
      default:
        return false;
    }
  };

  const renderOptions = (filter) => {
    if (!filter) return null;

    switch (filter.filter) {
      case "brand":
        return (
          <SpareBrandModelFilterOptions
            brandData={filter}
            appliedBrands={modalFilters.brand || []}
            appliedModels={modalFilters.model || []}
            onChange={(updated) =>
              setModalFilters((prev) => ({
                ...prev,
                ...updated,
              }))
            }
          />
        );

      case "spare":
        return (
          <SparePartOptions
            sparePartsData={filter}
            appliedSpares={modalFilters.spare || []}
            onChange={(updated) =>
              setModalFilters((prev) => ({
                ...prev,
                ...updated,
              }))
            }
          />
        );

      default:
        return null;
    }
  };

  // --------------------- MODAL OPEN ---------------------
  const handleOpenModal = (filterKey = null) => {
    setSelectedFilterKey(filterKey ?? quickTrayFilters[0]?.filter ?? null);

    // Sync MODAL with REDUX filter values
    setModalFilters({
      brand: masterCategoryState.brand || [],
      model: masterCategoryState.model || [],
      part_name: masterCategoryState.part_name || [],
    });

    setIsOpen(true);
  };

   const handleApply = () => {
    // 1️⃣ Write MODAL selections into Redux
    dispatch(setFilters(modalFilters));

    // 2️⃣ Update URL
    const params = new URLSearchParams();

    if (modalFilters.brand?.length) {
      params.set("brand", modalFilters.brand.join(",")); // if array
    }
    if (modalFilters.model?.length) {
      params.set("model", modalFilters.model.join(","));
    }
    if (modalFilters.part_name?.length) {
      params.set("part_name", modalFilters.part_name.join(","));
    }

    setSearchParams(params);

    // 3️⃣ Close modal
    setIsOpen(false);
  };

  // --------------------- ADD SPARES ---------------------
  const handleAdd = () => {
    dispatch(setVariantFilters({ category }));
    dispatch(
      onOpen({
        action: "Add",
        brand: null,
        model: null,
        ram: null,
        rom: null,
        color: null,
        part_name: null,
        price: null,
        original_price: null,
      })
    );
  };

  // --------------------- SYNC URL → REDUX ---------------------
  useEffect(() => {
    const newFilters = {
      brand: searchParams.get("brand") || null,
      model: searchParams.get("model") || null,
      part_name: searchParams.get("part_name") || null,
    };

    dispatch(setFilters(newFilters));
  }, [dispatch, searchParams]);

  // --------------------- BLOCK BODY SCROLL ---------------------
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "auto";
    return () => (document.body.style.overflow = "auto");
  }, [isOpen]);

  // --------------------- APPLY FILTERS ---------------------
 

  // ==========================================================
  //                    UI STRUCTURE (UNCHANGED)
  // ==========================================================
  return (
    <div className={classes.box}>
      {/* SPARES FILTER SECTION */}
      {isSpares && (
        <section className={classes.box__filters}>
          <section className={classes.box__filters__main}>
            <button
              type="button"
              className={classes.box__filters__tab}
              onClick={() => handleOpenModal()}
            >
              <img
                alt="filter icon"
                src={mobile_filter}
                className={classes.box__filters__tab__icon}
              />
              <h1 className={classes.box__filters__tab__title}>Filters</h1>
            </button>
            <span className={classes.box__filters__main__sep} />
          </section>

          {/* Quick Tray */}
          <section className={classes.box__quick}>
            <motion.div className={classes.box__filters__quick__tray}>
              {quickTrayFilters.map((filter) => {
                const active = isFilterActive(filter);
                return (
                  <div
                    key={filter.filter}
                    role="tab"
                    tabIndex={0}
                    onClick={() => handleOpenModal(filter.filter)}
                    className={`${classes.box__filters__quick__tray__btn} ${
                      active
                        ? classes.box__filters__quick__tray__btn__applied
                        : ""
                    }`}
                  >
                    <h3
                      className={`${
                        classes.box__filters__quick__tray__btn__text
                      } ${
                        active
                          ? classes.box__filters__quick__tray__btn__text__applied
                          : ""
                      }`}
                    >
                      {filter.label}
                    </h3>

                    <span
                      className={`${
                        classes.box__filters__quick__tray__btn__close
                      } ${
                        active
                          ? classes.box__filters__quick__tray__btn__close__active
                          : ""
                      }`}
                    >
                      ×
                    </span>

                    {!active && (
                      <span style={{ color: "#2f3567", paddingBottom: "4px" }}>
                        ⏷
                      </span>
                    )}
                  </div>
                );
              })}
            </motion.div>
          </section>

          <button className={classes.box__btn__add} onClick={handleAdd}>
            Add Spares
          </button>
        </section>
      )}

      {/* MODAL */}
      <AnimatePresence>
        {isOpen && (
          <motion.section
            className={classes.box__filters__overlay}
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <motion.section
              className={classes.box__filters__modal}
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {/* Header */}
              <div className={classes.box__filters__modal__head}>
                <div className={classes.box__filters__modal__head__content}>
                  <img
                    alt="filter icon"
                    src={mobile_filter}
                    className={classes.box__filters__tab__icon}
                  />
                  <h4 className={classes.box__filters__modal__head__title}>
                    Filters
                  </h4>
                </div>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className={classes.box__filters__modal__close}
                >
                  X
                </button>
              </div>

              {/* Content */}
              <div className={classes.box__filters__modal__content}>
                <div className={classes.box__space__wrapper}>
                  {/* LEFT MENU */}
                  <div className={classes.box__filters__modal__content__left}>
                    {quickTrayFilters.map((filter) => {
                      const isActive = selectedFilterKey === filter.filter;

                      return (
                        <button
                          key={filter.filter}
                          type="button"
                          onClick={() => setSelectedFilterKey(filter.filter)}
                          className={`${
                            classes.box__filters__modal__content__left__btn
                          } ${
                            isActive
                              ? classes.box__filters__modal__content__left__btn__active
                              : isFilterActive(filter)
                              ? classes.box__filters__modal__content__left__btn__applied
                              : ""
                          }`}
                        >
                          {filter.label}
                        </button>
                      );
                    })}
                  </div>

                  <div className={classes.box__filters__modal__content__sep} />
                </div>

                {/* RIGHT OPTIONS */}
                <div className={classes.box__filters__modal__content__right}>
                  {selectedFilterKey ? (
                    renderOptions(
                      filters.find((f) => f.filter === selectedFilterKey) ||
                        null
                    )
                  ) : (
                    <p className={classes.box__filters__modal__placeholder}>
                      Select a filter to view options
                    </p>
                  )}
                </div>
              </div>

              {/* FOOTER */}
              <div className={classes.box__filters__modal__footer}>
                <button
                  className={`${classes.box__filters__modal__footer__btn} ${classes.box__filters__modal__footer__btn__cancel}`}
                >
                  Clear All
                </button>
                <button
                  className={`${classes.box__filters__modal__footer__btn} ${classes.box__filters__modal__footer__btn__show}`}
                  onClick={handleApply}
                >
                  Apply
                </button>
              </div>
            </motion.section>
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  );
};
